import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import type { MonitoringActivityEvent, MonitoringActivityType } from '../../types/monitoring';
import {
  activityEventKey,
  announcementTypeLabel,
  formatMonitoringDateTime,
  resolveMonitoringImageUrl,
} from '../../utils/monitoringPresentation';

function activityLabel(type: MonitoringActivityType): string {
  switch (type) {
    case 'announcement_created':
      return 'Annonce';
    case 'matching_created':
      return 'Matching';
    case 'conversation_started':
      return 'Conversation';
    case 'payment_completed':
      return 'Paiement';
    default:
      return 'Activité';
  }
}

function activityColor(type: MonitoringActivityType): string {
  switch (type) {
    case 'announcement_created':
      return '#64748b';
    case 'matching_created':
      return '#0ea5e9';
    case 'conversation_started':
      return '#8b5cf6';
    case 'payment_completed':
      return '#16a34a';
    default:
      return '#94a3b8';
  }
}

type Props = {
  events: MonitoringActivityEvent[];
  periodLabel?: string;
  selectedEventKey?: string | null;
  onEventSelect?: (event: MonitoringActivityEvent, eventKey: string) => void;
};

const ACTIVITY_PREVIEW_COUNT = 5;
const ACTIVITY_ROW_HEIGHT_PX = 84;
const VISIBLE_ACTIVITY_ROWS = 5;
const ACTIVITY_LIST_HEIGHT_PX = ACTIVITY_ROW_HEIGHT_PX * VISIBLE_ACTIVITY_ROWS;

const MonitoringActivityFeed: React.FC<Props> = ({
  events,
  periodLabel,
  selectedEventKey = null,
  onEventSelect,
}) => {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLUListElement | null>(null);
  const selectedItemRef = useRef<HTMLDivElement | null>(null);

  const visibleEvents = useMemo(
    () => (expanded ? events : events.slice(0, ACTIVITY_PREVIEW_COUNT)),
    [events, expanded]
  );

  useEffect(() => {
    if (!expanded || !selectedEventKey) return;
    selectedItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expanded, selectedEventKey, visibleEvents.length]);

  const handleEventClick = (event: MonitoringActivityEvent) => {
    const originalIndex = events.indexOf(event);
    const eventKey = activityEventKey(event, originalIndex >= 0 ? originalIndex : 0);
    if (!expanded) {
      setExpanded(true);
    }
    onEventSelect?.(event, eventKey);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Activité récente{periodLabel ? ` — ${periodLabel}` : ''}
          </Typography>
          {!expanded && events.length > ACTIVITY_PREVIEW_COUNT ? (
            <Typography variant="caption" color="text.secondary">
              Cliquez sur un événement pour afficher les {events.length} événements
            </Typography>
          ) : null}
        </Box>
        {events.length > ACTIVITY_PREVIEW_COUNT ? (
          <Button
            size="small"
            variant={expanded ? 'outlined' : 'text'}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Réduire' : `Tout (${events.length})`}
          </Button>
        ) : null}
      </Box>
      <List
        ref={listRef}
        dense
        sx={{
          overflowY: 'auto',
          height: ACTIVITY_LIST_HEIGHT_PX,
          maxHeight: ACTIVITY_LIST_HEIGHT_PX,
          py: 0,
          m: 0,
        }}
      >
        {visibleEvents.length === 0 ? (
          <ListItemButton disabled sx={{ py: 2 }}>
            <ListItemText
              primary="Aucune activité récente"
              secondary="Les nouveaux événements apparaîtront ici."
            />
          </ListItemButton>
        ) : (
          visibleEvents.map((event) => {
            const originalIndex = events.indexOf(event);
            const eventKey = activityEventKey(event, originalIndex >= 0 ? originalIndex : 0);
            const selected = selectedEventKey === eventKey;
            const imageUrl = resolveMonitoringImageUrl(event.image_url);

            return (
              <ListItemButton
                key={eventKey}
                ref={selected ? selectedItemRef : undefined}
                selected={selected}
                alignItems="flex-start"
                onClick={() => handleEventClick(event)}
                sx={{
                  py: 1,
                  minHeight: ACTIVITY_ROW_HEIGHT_PX,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Stack direction="row" spacing={1.25} sx={{ width: '100%', alignItems: 'flex-start' }}>
                  <Avatar
                    variant="rounded"
                    src={imageUrl ?? undefined}
                    sx={{
                      width: 52,
                      height: 52,
                      bgcolor: '#e2e8f0',
                      color: '#64748b',
                      fontSize: '0.75rem',
                      flexShrink: 0,
                    }}
                  >
                    {activityLabel(event.type).slice(0, 1)}
                  </Avatar>
                  <ListItemText
                    sx={{ my: 0 }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.35, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={activityLabel(event.type)}
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: activityColor(event.type),
                            color: '#fff',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatMonitoringDateTime(event.at)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.35 }}>
                          {event.label}
                          {event.type === 'payment_completed' && event.meta?.amount_eur != null
                            ? ` — ${Number(event.meta.amount_eur).toFixed(0)} €`
                            : ''}
                          {event.type === 'announcement_created' && event.meta?.announcement_type != null
                            ? ` (${announcementTypeLabel(String(event.meta.announcement_type))})`
                            : ''}
                        </Typography>
                        {event.description ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.35, lineHeight: 1.4 }}
                          >
                            {event.description}
                          </Typography>
                        ) : null}
                        {event.type === 'matching_created' && event.meta?.target_title != null ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            Lié à : {String(event.meta.target_title)}
                            {event.meta?.score != null ? ` · ${Math.round(Number(event.meta.score))} %` : ''}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                  />
                </Stack>
              </ListItemButton>
            );
          })
        )}
      </List>
    </Box>
  );
};

export default MonitoringActivityFeed;
